/**
 * ReassignmentImpactModal
 * 
 * Displays the impact preview of an operator reassignment before execution.
 * Shows affected packs, campaigns, cycles, and mobile assignments.
 * After approval, shows real-time pipeline progress.
 * 
 * Flow:
 * 1. Admin triggers reassignment (changes operator assignments)
 * 2. This modal appears with impact preview from POST /api/operator-reassignment/preview
 * 3. Admin reviews impact and either approves or cancels
 * 4. On approve, executes POST /api/operator-reassignment/:id/approve
 * 5. Modal switches to progress view, polling GET /api/operator-reassignment/:id/status
 */

import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { AlertTriangle, Package, FileText, Calendar, Smartphone, ArrowRight, Loader2, XCircle } from 'lucide-react';
import { apiRequest } from '@/lib/queryClient';
import { ReassignmentProgress, type StageProgressMap } from './ReassignmentProgress';

// Types matching the backend ReassignmentImpact
export interface AssignmentChange {
  storeId: number;
  mediaFormatId: number;
  newOperatorId: number;
  currentOperatorId?: number;
  storeName?: string;
  formatName?: string;
  currentOperatorName?: string;
  newOperatorName?: string;
}

export interface AffectedPack {
  packId: number;
  packName: string;
  currentOperatorIds: number[];
  affectedStoreCount: number;
}

export interface AffectedCampaign {
  campaignId: number;
  campaignName: string;
  cycleId: number | null;
  cycleName?: string;
  affectedPackIds: number[];
}

export interface AffectedCycle {
  cycleId: number;
  cycleName: string;
  lockedAt: string;
  affectedCampaignCount: number;
}

export interface AffectedMobileAssignment {
  mobileAssignmentId: number;
  operatorId: number;
  operatorName?: string;
  status: string;
}

export interface ReassignmentImpact {
  changes: AssignmentChange[];
  affectedPacks: AffectedPack[];
  affectedCampaigns: AffectedCampaign[];
  affectedCycles: AffectedCycle[];
  affectedMobileAssignments: AffectedMobileAssignment[];
  summary: {
    totalChanges: number;
    packsToRegenerate: number;
    campaignsToRegenerate: number;
    cyclesToRelock: number;
    mobileAssignmentsToSupersede: number;
  };
}

export interface ReassignmentPreviewResponse {
  reassignmentId: number;
  status: string;
  impact: ReassignmentImpact;
  message: string;
}

interface FailureDetails {
  failedAt: string | null;
  failedAtStage: string | null;
  failureReason: string | null;
  stageProgress: StageProgressMap;
}

interface ReassignmentStatusResponse {
  reassignmentId: number;
  status: 'previewed' | 'approved' | 'in_progress' | 'completed' | 'failed' | 'cancelled';
  initiatedAt: string;
  completedAt: string | null;
  stageProgress: StageProgressMap;
  errorMessage?: string;
  failureDetails?: FailureDetails | null;
}

export interface ReassignmentImpactModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  changes: AssignmentChange[];
  cycleId?: number;
  retailerId?: number;
  onComplete?: () => void;
}

type ModalPhase = 'preview' | 'executing';

export function ReassignmentImpactModal({
  open,
  onOpenChange,
  changes,
  cycleId,
  retailerId,
  onComplete,
}: ReassignmentImpactModalProps) {
  const queryClient = useQueryClient();
  const [previewData, setPreviewData] = useState<ReassignmentPreviewResponse | null>(null);
  const [phase, setPhase] = useState<ModalPhase>('preview');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Preview mutation - called when modal opens
  const previewMutation = useMutation({
    mutationFn: async (changes: AssignmentChange[]) => {
      const response = await apiRequest('/api/operator-reassignment/preview', 'POST', { 
        changes,
        cycleId,
        retailerId,
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || error.error || 'Failed to preview reassignment');
      }
      return response.json() as Promise<ReassignmentPreviewResponse>;
    },
    onSuccess: (data) => {
      setPreviewData(data);
    },
    onError: (error: Error) => {
      setErrorMessage(error.message);
    },
  });

  // Approve mutation - starts the pipeline execution
  const approveMutation = useMutation({
    mutationFn: async (reassignmentId: number) => {
      const response = await apiRequest(`/api/operator-reassignment/${reassignmentId}/approve`, 'POST', {});
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || error.error || 'Something went wrong. Please dismiss and try again.');
      }
      return response.json();
    },
    onSuccess: () => {
      setPhase('executing');
    },
    onError: (error: Error) => {
      // Pipeline failed — switch to executing phase so the user sees
      // the failure view with "Dismiss & Start Over" instead of a dead-end error
      setPhase('executing');
      setErrorMessage(error.message);
    },
  });

  // Status polling query - only active during execution phase
  const statusQuery = useQuery<ReassignmentStatusResponse>({
    queryKey: ['operator-reassignment', previewData?.reassignmentId, 'status'],
    queryFn: async () => {
      if (!previewData?.reassignmentId) throw new Error('No reassignment ID');
      const response = await apiRequest(`/api/operator-reassignment/${previewData.reassignmentId}/status`, 'GET');
      if (!response.ok) {
        throw new Error('Failed to get reassignment status');
      }
      return response.json();
    },
    enabled: phase === 'executing' && !!previewData?.reassignmentId,
    refetchInterval: (query) => {
      const data = query.state.data;
      // Poll every 1 second while in_progress, stop when done
      if (data?.status === 'completed' || data?.status === 'failed') {
        return false;
      }
      return 1000;
    },
  });

  // Handle completion
  useEffect(() => {
    if (statusQuery.data?.status === 'completed') {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['/api/store-format-operator-assignments'] });
      queryClient.invalidateQueries({ queryKey: ['/api/packs'] });
      queryClient.invalidateQueries({ queryKey: ['/api/campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['/api/locked-cycles'] });
      
      // Close modal after a short delay
      setTimeout(() => {
        onOpenChange(false);
        onComplete?.();
      }, 2500);
    }
  }, [statusQuery.data?.status, queryClient, onOpenChange, onComplete]);

  // Cancel mutation - cancels a previewed reassignment
  const cancelMutation = useMutation({
    mutationFn: async (reassignmentId: number) => {
      const response = await apiRequest(`/api/operator-reassignment/${reassignmentId}/cancel`, 'POST', {});
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || error.error || 'Failed to cancel reassignment');
      }
      return response.json();
    },
  });

  // Trigger preview when modal opens with changes
  useEffect(() => {
    if (open && changes.length > 0 && !previewData && !previewMutation.isPending) {
      setPhase('preview');
      setErrorMessage(null);
      previewMutation.mutate(changes);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, changes.length]);

  // Handle dialog close
  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      // Cancel the preview if closing without approving
      if (previewData && phase === 'preview') {
        cancelMutation.mutate(previewData.reassignmentId);
      }
      // Reset state when closing
      setPreviewData(null);
      setPhase('preview');
      setErrorMessage(null);
    }
    onOpenChange(isOpen);
  };

  const handleApprove = () => {
    if (previewData) {
      approveMutation.mutate(previewData.reassignmentId);
    }
  };

  // Handle "Start Over" for failed reassignments
  const handleStartOver = async () => {
    if (previewData) {
      try {
        await cancelMutation.mutateAsync(previewData.reassignmentId);
        // Reset state and trigger new preview
        setPreviewData(null);
        setPhase('preview');
        setErrorMessage(null);
        // Trigger new preview
        previewMutation.mutate(changes);
      } catch (error) {
        setErrorMessage(error instanceof Error ? error.message : 'Failed to dismiss reassignment');
      }
    }
  };

  const impact = previewData?.impact;
  // Backend returns nested structure: { reassignment: { status, stageProgress, ... } }
  const reassignmentData = (statusQuery.data as any)?.reassignment;
  // If approve itself failed (no poll data yet), treat as failed so the user
  // sees the failure view with "Dismiss & Start Over" instead of a stuck spinner
  const executionStatus = reassignmentData?.status
    ?? (phase === 'executing' && errorMessage ? 'failed' : 'approved');
  const stageProgress = reassignmentData?.stageProgress ?? {};
  const failureDetails = reassignmentData?.failureDetails;

  // Determine if we can close the modal
  const canClose = phase === 'preview' || 
    executionStatus === 'completed' || 
    executionStatus === 'failed';

  return (
    <Dialog open={open} onOpenChange={canClose ? handleOpenChange : undefined}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {phase === 'preview' && !errorMessage ? (
              <>
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                Operator Reassignment Impact
              </>
            ) : phase === 'preview' && errorMessage ? (
              <>
                <XCircle className="h-5 w-5 text-red-500" />
                Reassignment Error
              </>
            ) : executionStatus === 'failed' ? (
              <>
                <XCircle className="h-5 w-5 text-red-500" />
                Reassignment Failed
              </>
            ) : executionStatus === 'completed' ? (
              <>
                <Package className="h-5 w-5 text-green-500" />
                Reassignment Complete
              </>
            ) : (
              <>
                <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />
                Executing Reassignment
              </>
            )}
          </DialogTitle>
          <DialogDescription>
            {phase === 'preview' && !errorMessage
              ? 'Review the changes that will be made to the system. This operation is forward-only and cannot be undone.'
              : phase === 'preview' && errorMessage
              ? 'An error occurred. You can dismiss this and try again.'
              : executionStatus === 'failed'
              ? 'The reassignment pipeline encountered an error. Review the details below and try again.'
              : executionStatus === 'completed'
              ? 'All changes have been applied successfully.'
              : 'The reassignment pipeline is running. Please wait for all stages to complete.'}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 pr-4">
          {/* Preview Loading State */}
          {phase === 'preview' && previewMutation.isPending && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <span className="ml-2 text-muted-foreground">Analyzing impact...</span>
            </div>
          )}

          {/* Preview Error State */}
          {phase === 'preview' && errorMessage && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <div className="flex items-center gap-2 text-red-800">
                <XCircle className="h-5 w-5" />
                <span className="font-medium">Error</span>
              </div>
              <p className="text-red-700 mt-1">{errorMessage}</p>
            </div>
          )}

          {/* Execution Progress View */}
          {phase === 'executing' && (
            <ReassignmentProgress
              stageProgress={stageProgress}
              status={executionStatus}
              errorMessage={reassignmentData?.failureReason || errorMessage || undefined}
              onComplete={() => {
                // Handled by useEffect above
              }}
            />
          )}

          {/* Preview Impact View */}
          {phase === 'preview' && impact && (
            <div className="space-y-4">
              {/* Summary Badges */}
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline" className="text-sm">
                  {impact.summary.totalChanges} assignment{impact.summary.totalChanges !== 1 ? 's' : ''} changed
                </Badge>
                {impact.summary.packsToRegenerate > 0 && (
                  <Badge variant="secondary" className="text-sm">
                    <Package className="h-3 w-3 mr-1" />
                    {impact.summary.packsToRegenerate} pack{impact.summary.packsToRegenerate !== 1 ? 's' : ''} regenerated
                  </Badge>
                )}
                {impact.summary.campaignsToRegenerate > 0 && (
                  <Badge variant="secondary" className="text-sm">
                    <FileText className="h-3 w-3 mr-1" />
                    {impact.summary.campaignsToRegenerate} campaign{impact.summary.campaignsToRegenerate !== 1 ? 's' : ''} regenerated
                  </Badge>
                )}
                {impact.summary.cyclesToRelock > 0 && (
                  <Badge variant="secondary" className="text-sm">
                    <Calendar className="h-3 w-3 mr-1" />
                    {impact.summary.cyclesToRelock} cycle{impact.summary.cyclesToRelock !== 1 ? 's' : ''} relocked
                  </Badge>
                )}
                {impact.summary.mobileAssignmentsToSupersede > 0 && (
                  <Badge variant="destructive" className="text-sm">
                    <Smartphone className="h-3 w-3 mr-1" />
                    {impact.summary.mobileAssignmentsToSupersede} mobile task{impact.summary.mobileAssignmentsToSupersede !== 1 ? 's' : ''} superseded
                  </Badge>
                )}
              </div>

              <Separator />

              <Accordion type="multiple" defaultValue={['changes']} className="w-full">
                {/* Assignment Changes */}
                <AccordionItem value="changes">
                  <AccordionTrigger className="text-sm font-medium">
                    Assignment Changes ({impact.changes.length})
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-2">
                      {impact.changes.map((change, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm bg-muted/50 rounded px-3 py-2">
                          <span className="font-medium truncate max-w-[150px]" title={change.storeName}>
                            {change.storeName || `Store ${change.storeId}`}
                          </span>
                          <span className="text-muted-foreground">•</span>
                          <span className="truncate max-w-[100px]" title={change.formatName}>
                            {change.formatName || `Format ${change.mediaFormatId}`}
                          </span>
                          <ArrowRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                          <span className="text-amber-600 truncate max-w-[100px]" title={change.currentOperatorName}>
                            {change.currentOperatorName || `Op ${change.currentOperatorId || '?'}`}
                          </span>
                          <ArrowRight className="h-4 w-4 text-green-600 flex-shrink-0" />
                          <span className="text-green-600 font-medium truncate max-w-[100px]" title={change.newOperatorName}>
                            {change.newOperatorName || `Op ${change.newOperatorId}`}
                          </span>
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Affected Packs */}
                {impact.affectedPacks.length > 0 && (
                  <AccordionItem value="packs">
                    <AccordionTrigger className="text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <Package className="h-4 w-4" />
                        Affected Packs ({impact.affectedPacks.length})
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-2">
                        {impact.affectedPacks.map((pack) => (
                          <div key={pack.packId} className="flex items-center justify-between text-sm bg-muted/50 rounded px-3 py-2">
                            <span className="font-medium">{pack.packName}</span>
                            <Badge variant="outline">{pack.affectedStoreCount} store{pack.affectedStoreCount !== 1 ? 's' : ''}</Badge>
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                )}

                {/* Affected Campaigns */}
                {impact.affectedCampaigns.length > 0 && (
                  <AccordionItem value="campaigns">
                    <AccordionTrigger className="text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4" />
                        Affected Campaigns ({impact.affectedCampaigns.length})
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-2">
                        {impact.affectedCampaigns.map((campaign) => (
                          <div key={campaign.campaignId} className="flex items-center justify-between text-sm bg-muted/50 rounded px-3 py-2">
                            <div>
                              <span className="font-medium">{campaign.campaignName}</span>
                              {campaign.cycleName && (
                                <span className="text-muted-foreground ml-2">({campaign.cycleName})</span>
                              )}
                            </div>
                            <Badge variant="outline">{campaign.affectedPackIds.length} pack{campaign.affectedPackIds.length !== 1 ? 's' : ''}</Badge>
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                )}

                {/* Affected Cycles (Locked) */}
                {impact.affectedCycles.length > 0 && (
                  <AccordionItem value="cycles">
                    <AccordionTrigger className="text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        Locked Cycles to Relock ({impact.affectedCycles.length})
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-2">
                        {impact.affectedCycles.map((cycle) => (
                          <div key={cycle.cycleId} className="flex items-center justify-between text-sm bg-muted/50 rounded px-3 py-2">
                            <div>
                              <span className="font-medium">{cycle.cycleName}</span>
                              <span className="text-muted-foreground ml-2 text-xs">
                                Locked {new Date(cycle.lockedAt).toLocaleDateString()}
                              </span>
                            </div>
                            <Badge variant="outline">{cycle.affectedCampaignCount} campaign{cycle.affectedCampaignCount !== 1 ? 's' : ''}</Badge>
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                )}

                {/* Mobile Assignments Warning */}
                {impact.affectedMobileAssignments.length > 0 && (
                  <AccordionItem value="mobile">
                    <AccordionTrigger className="text-sm font-medium text-amber-600">
                      <div className="flex items-center gap-2">
                        <Smartphone className="h-4 w-4" />
                        Mobile Tasks to Supersede ({impact.affectedMobileAssignments.length})
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-2">
                        <p className="text-amber-800 text-sm">
                          <strong>Warning:</strong> These tasks are currently assigned to operators on the mobile app. 
                          They will be marked as superseded and operators will receive new assignments.
                        </p>
                      </div>
                      <div className="space-y-2">
                        {impact.affectedMobileAssignments.slice(0, 10).map((ma) => (
                          <div key={ma.mobileAssignmentId} className="flex items-center justify-between text-sm bg-muted/50 rounded px-3 py-2">
                            <span>{ma.operatorName || `Operator ${ma.operatorId}`}</span>
                            <Badge variant="outline">{ma.status}</Badge>
                          </div>
                        ))}
                        {impact.affectedMobileAssignments.length > 10 && (
                          <p className="text-muted-foreground text-sm text-center">
                            ...and {impact.affectedMobileAssignments.length - 10} more
                          </p>
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                )}
              </Accordion>
            </div>
          )}
        </ScrollArea>

        <DialogFooter className="mt-4">
          {phase === 'preview' && !errorMessage && (
            <>
              <Button
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={approveMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                onClick={handleApprove}
                disabled={!previewData || previewMutation.isPending || approveMutation.isPending}
                className="bg-amber-600 hover:bg-amber-700"
              >
                {approveMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Starting...
                  </>
                ) : (
                  'Approve & Execute'
                )}
              </Button>
            </>
          )}
          {/* Error state in preview phase - allow dismiss */}
          {phase === 'preview' && errorMessage && (
            <Button
              variant="outline"
              onClick={() => handleOpenChange(false)}
            >
              Close
            </Button>
          )}
          {/* Executing phase - show appropriate buttons based on status */}
          {phase === 'executing' && executionStatus !== 'failed' && (
            <Button
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={executionStatus === 'in_progress' || executionStatus === 'approved'}
            >
              {executionStatus === 'completed' ? 'Close' : 'Executing...'}
            </Button>
          )}
          {/* Failed state - show Dismiss & Start Over button */}
          {phase === 'executing' && executionStatus === 'failed' && (
            <>
              <Button
                variant="outline"
                onClick={() => handleOpenChange(false)}
              >
                Close
              </Button>
              <Button
                onClick={handleStartOver}
                disabled={cancelMutation.isPending}
                className="bg-amber-600 hover:bg-amber-700"
              >
                {cancelMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Dismissing...
                  </>
                ) : (
                  'Dismiss & Start Over'
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
